import { useState, FormEvent } from "react";

interface ReservationFormProps {
  restaurantName: string;
  restaurantCity: string;
}

const timeSlots = [
  "12:00", "12:30", "13:00", "13:30", "14:00",
  "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00",
];

const guestOptions = ["1 Kişi", "2 Kişi", "3 Kişi", "4 Kişi", "5 Kişi", "6 Kişi", "7+ Kişi"];

type SubmitStatus = "idle" | "loading" | "success" | "error";

const ReservationForm = ({ restaurantName, restaurantCity }: ReservationFormProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const today = new Date().toISOString().split("T")[0];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const note = (form.elements.namedItem("note") as HTMLTextAreaElement)?.value || "";
    if (note.length > 500) return;

    setSubmitStatus("loading");

    const data = new URLSearchParams();
    const fields = ["name", "email", "phone", "date", "time", "guests", "note"];
    fields.forEach((field) => {
      const el = form.elements.namedItem(field) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (el) data.append(field, el.value);
    });
    data.append("restaurant", restaurantName);
    data.append("city", restaurantCity);

    try {
      const res = await fetch("https://readdy.ai/api/form/d7d3uokkpdh0a4advqu0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    }
  };

  return (
    <div className="mt-14">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <h2
          className="text-xl font-bold text-stone-900"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Rezervasyon Yap
        </h2>
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-5 py-2.5 rounded-full cursor-pointer transition-colors whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-check-line"></i>
            </div>
            Masa Ayırt
          </button>
        )}
      </div>

      {/* Collapsed teaser */}
      {!isOpen && (
        <div
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-4 p-5 bg-amber-50 border border-amber-100 rounded-2xl cursor-pointer hover:border-amber-300 transition-colors"
        >
          <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
            <i className="ri-calendar-2-line text-amber-600 text-xl"></i>
          </div>
          <div className="flex-1">
            <p className="font-semibold text-stone-900 text-sm">{restaurantName} için masa ayırtın</p>
            <p className="text-stone-500 text-xs mt-0.5">
              Rezervasyon talebiniz doğrudan restorana iletilir. Onay için sizi arayacaklar.
            </p>
          </div>
          <div className="w-8 h-8 flex items-center justify-center text-amber-500">
            <i className="ri-arrow-right-s-line text-xl"></i>
          </div>
        </div>
      )}

      {/* Expanded Form */}
      {isOpen && (
        <div className="bg-stone-50 rounded-2xl border border-stone-100 overflow-hidden">
          {/* Form Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-amber-500">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
                <i className="ri-calendar-check-line text-white text-sm"></i>
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{restaurantName}</p>
                <p className="text-amber-100 text-xs">{restaurantCity} · Rezervasyon Formu</p>
              </div>
            </div>
            <button
              onClick={() => { setIsOpen(false); setSubmitStatus("idle"); }}
              className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Success State */}
          {submitStatus === "success" ? (
            <div className="px-6 py-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                <i className="ri-check-line text-emerald-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Rezervasyon Talebiniz Alındı!
              </h3>
              <p className="text-stone-500 text-sm max-w-sm mx-auto leading-relaxed">
                Talebiniz <strong>{restaurantName}</strong>'a iletildi. Restoran en kısa sürede sizi arayarak onaylayacak.
              </p>
              <button
                onClick={() => { setIsOpen(false); setSubmitStatus("idle"); }}
                className="mt-6 px-6 py-2.5 bg-amber-500 text-white text-sm font-semibold rounded-full cursor-pointer hover:bg-amber-400 transition-colors whitespace-nowrap"
              >
                Tamam
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="px-6 py-6 space-y-5"
            >
              {/* Name + Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">
                    Ad Soyad <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Adınız ve soyadınız"
                    className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">
                    Telefon <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="05XX XXX XX XX"
                    className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">
                  E-posta <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="ornek@email.com"
                  className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              {/* Date + Time + Guests */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">
                    Tarih <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    required
                    min={today}
                    className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">
                    Saat <span className="text-rose-500">*</span>
                  </label>
                  <select
                    name="time"
                    required
                    className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors cursor-pointer"
                  >
                    <option value="">Saat seçin</option>
                    <optgroup label="Öğle">
                      {timeSlots.slice(0, 5).map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Akşam">
                      {timeSlots.slice(5).map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">
                    Kişi Sayısı <span className="text-rose-500">*</span>
                  </label>
                  <select
                    name="guests"
                    required
                    className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors cursor-pointer"
                  >
                    <option value="">Seçin</option>
                    {guestOptions.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">
                  Özel İstek / Not
                  <span className="text-stone-400 font-normal ml-1">(isteğe bağlı)</span>
                </label>
                <textarea
                  name="note"
                  rows={3}
                  maxLength={500}
                  placeholder="Alerji, özel gün, oturma tercihi vb. notlarınızı yazabilirsiniz..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors resize-none"
                />
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-stone-400">Maksimum 500 karakter</p>
                  <span className={`text-xs ${charCount > 480 ? "text-rose-500" : "text-stone-400"}`}>
                    {charCount}/500
                  </span>
                </div>
              </div>

              {/* Info note */}
              <div className="flex items-start gap-2.5 p-3.5 bg-amber-50 rounded-xl border border-amber-100">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-amber-600 text-sm"></i>
                </div>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Rezervasyon talebiniz <strong>{restaurantName}</strong>'a iletilecektir. Restoran en kısa sürede sizi arayarak rezervasyonunuzu onaylayacak veya alternatif önerecektir.
                </p>
              </div>

              {/* Error */}
              {submitStatus === "error" && (
                <div className="flex items-center gap-2 p-3 bg-rose-50 rounded-xl border border-rose-100">
                  <i className="ri-error-warning-line text-rose-500 text-sm"></i>
                  <p className="text-xs text-rose-600">Bir hata oluştu. Lütfen tekrar deneyin.</p>
                </div>
              )}

              {/* Submit */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={submitStatus === "loading" || charCount > 500}
                  className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:bg-stone-300 text-white font-semibold text-sm py-3 rounded-xl cursor-pointer transition-colors whitespace-nowrap"
                >
                  {submitStatus === "loading" ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-send-plane-line"></i>
                      </div>
                      Rezervasyon Talebi Gönder
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setIsOpen(false); setSubmitStatus("idle"); }}
                  className="px-4 py-3 text-sm text-stone-500 hover:text-stone-700 cursor-pointer transition-colors whitespace-nowrap"
                >
                  İptal
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default ReservationForm;
